// THE ONE PATTERN for rendering a data fetch. Loading / ERROR / empty / data.
//
// WHAT THIS FIXES. Across 49 page components the render was:
//
//     {isLoading && <p>Loading…</p>}
//     {data && <Table rows={data.items} />}
//
// React Query sets `isLoading=false`, `isError=true`, `data=undefined` on a
// failed fetch — so a 500 rendered NEITHER branch. The page showed a bare
// header, or a spinner that never resolved, and nothing anywhere said the
// request had failed. Escalation Matrix (audit D3) destructured only `{ data }`
// and so could not tell "no escalation rules" from "the request 500'd".
//
// That is worse than an error page: an empty table reads as a FACT ABOUT THE
// FLEET. An evaluator seeing "0 alerts" concludes the system is quiet, not that
// it is broken — which is exactly what happened when the usage_counters RLS 500
// degraded every feature-gated page at once.
//
// THE FOUR STATES ARE DISTINCT ON PURPOSE:
//   loading  the request is in flight            -> skeleton
//   error    it failed                            -> say so, name it, offer retry
//   empty    it SUCCEEDED and returned nothing    -> "no X yet"
//   data     render children
//
// `empty` and `error` must never render alike. That distinction is the whole
// point of this component.
import type { ReactNode } from "react";

import { AlertTriangleIcon, InboxIcon, RefreshCwIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export interface QueryStateProps {
  /** React Query's `isLoading` (or `isPending`). */
  isLoading: boolean;
  /** React Query's `isError`. */
  isError?: boolean;
  /** The thrown error, if any — used for the detail line. */
  error?: unknown;
  /** What was being fetched, in OPERATOR words: "alerts", "camera health".
   *  Appears in the error text, so "the alerts could not be loaded" reads as a
   *  sentence rather than as a stack trace. */
  label: string;
  /** React Query's `refetch`. Omit and no retry button is shown. */
  onRetry?: () => void;
  /** True when the fetch SUCCEEDED and returned zero rows. */
  isEmpty?: boolean;
  /** Operator-facing copy for the empty case. */
  emptyMessage?: string;
  children: ReactNode;
}

/** Pull a short, safe message out of whatever React Query threw. */
export function errorDetail(error: unknown): string | null {
  if (!error) return null;
  const anyErr = error as {
    response?: { status?: number; data?: { detail?: unknown } };
    message?: string;
  };
  const status = anyErr?.response?.status;
  const detail = anyErr?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return status ? `${status}: ${detail}` : detail;
  }
  if (status) return `HTTP ${status}`;
  if (typeof anyErr?.message === "string" && anyErr.message.trim()) {
    return anyErr.message;
  }
  return null;
}

export function QueryState({
  isLoading,
  isError = false,
  error,
  label,
  onRetry,
  isEmpty = false,
  emptyMessage,
  children,
}: QueryStateProps) {
  // ERROR IS CHECKED FIRST. React Query can hold stale `data` alongside a
  // failed refetch; showing that as though it were current is the false-green
  // this component exists to prevent.
  if (isError) {
    const detail = errorDetail(error);
    return (
      <Card data-testid="query-error">
        <CardContent className="flex flex-col items-center gap-2 py-8 text-center">
          <AlertTriangleIcon className="h-6 w-6 text-destructive" />
          <p className="text-sm font-medium">
            Could not load {label}
          </p>
          {detail && (
            <p className="text-xs text-muted-foreground" data-testid="query-error-detail">
              {detail}
            </p>
          )}
          {onRetry && (
            <Button variant="outline" size="sm" onClick={onRetry} className="mt-1">
              <RefreshCwIcon className="mr-1 h-3 w-3" />
              Retry
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card data-testid="query-loading">
        <CardContent className="py-8">
          <div className="mx-auto h-1.5 w-32 animate-pulse rounded-full bg-muted" />
        </CardContent>
      </Card>
    );
  }

  if (isEmpty) {
    return (
      <Card data-testid="query-empty">
        <CardContent className="flex flex-col items-center gap-2 py-8 text-center">
          <InboxIcon className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {emptyMessage ?? `No ${label} yet`}
          </p>
        </CardContent>
      </Card>
    );
  }

  return <>{children}</>;
}
